import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Plus, Briefcase, MoreVertical, ArrowRightCircle, XCircle,
  Eye, AlertTriangle, Phone, Mail, ShieldCheck,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import { STAFFING_ROLE_OPTIONS } from '../../config/publicContact';
import StaffingPipelineGuide from '../../components/portal/StaffingPipelineGuide';

const API = API_BASE;

const REQUEST_TYPES = [
  { value: 'one_off_shift',         label: 'One-Off Shift' },
  { value: 'ongoing_cover',         label: 'Ongoing Cover' },
  { value: 'emergency_cover',       label: 'Emergency Cover' },
  { value: 'permanent_recruitment', label: 'Permanent Recruitment' },
];

const REQUEST_TYPE_LABELS = Object.fromEntries(REQUEST_TYPES.map(({ value, label }) => [value, label]));

const ORG_TYPE_LABELS = {
  care_home:                  'Care Home',
  supported_living_provider:  'Supported Living',
  domiciliary_care_provider:  'Domiciliary Care',
  nhs_trust:                  'NHS Trust',
  local_authority:            'Local Authority',
  private_provider:           'Private Provider',
  charity:                    'Charity',
  other:                      'Other',
};

// request_type values that require specific start/end datetime
const NEEDS_DATETIME = new Set(['one_off_shift', 'emergency_cover']);

const STATUS_COLORS = {
  open:       'bg-blue-100 text-blue-700',
  in_review:  'bg-amber-100 text-amber-700',
  matched:    'bg-purple-100 text-purple-700',
  fulfilled:  'bg-green-100 text-green-700',
  cancelled:  'bg-gray-100 text-gray-500',
};

const STATUS_LABELS = {
  open:       'Open',
  in_review:  'In Review',
  matched:    'Matched / Partial Cover',
  fulfilled:  'Fulfilled',
  cancelled:  'Cancelled',
};

const ALL_STATUSES = ['open', 'in_review', 'matched', 'fulfilled', 'cancelled'];
const TERMINAL_STATUSES = new Set(['cancelled', 'fulfilled']);

function getShiftsCreated(req) {
  return Number(req?.shifts_created ?? 0);
}

function getRequestQuantity(req) {
  return Math.max(1, Number(req?.quantity ?? 1));
}

function canConvertRequest(req) {
  if (!req || TERMINAL_STATUSES.has(req.status)) return false;
  return getShiftsCreated(req) < getRequestQuantity(req);
}

function formatShiftProgress(req) {
  return `${getShiftsCreated(req)}/${getRequestQuantity(req)}`;
}

function formatCoverProgress(req) {
  return `${Number(req?.shifts_covered ?? 0)}/${getRequestQuantity(req)} covered`;
}

function requestStatusLabel(req) {
  if (req?.request_type !== 'permanent_recruitment') {
    if (req?.status === 'fulfilled') return 'Cover Booked';
    if (req?.status === 'matched') return 'Partially Covered';
  }
  return STATUS_LABELS[req?.status] || req?.status;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function toDatetimeLocal(iso) {
  if (!iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function toIso(datetimeLocal) {
  if (!datetimeLocal) return null;
  const dt = new Date(datetimeLocal);
  if (isNaN(dt.getTime())) return null;
  return dt.toISOString();
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
  catch { return iso; }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

// ── Form defaults ─────────────────────────────────────────────────────────────

const EMPTY_CREATE_FORM = {
  organisation_id: '',
  request_type: '',
  role_required: '',
  quantity: 1,
  requested_start_at: '',
  requested_end_at: '',
  notes: '',
};

const EMPTY_CONVERT_FORM = {
  start_at: '',
  end_at: '',
  location_text: '',
  notes: '',
  care_location_id: '',
  count: 1,
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function StaffingRequestsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [careLocations, setCareLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);

  // Detail drawer
  const [drawerRequest, setDrawerRequest] = useState(null);
  const [drawerNotes, setDrawerNotes] = useState('');
  const [drawerNotesOriginal, setDrawerNotesOriginal] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [verifyingOrg, setVerifyingOrg] = useState(false);

  // Cancel confirmation
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Convert-to-shift dialog
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertForm, setConvertForm] = useState(EMPTY_CONVERT_FORM);
  const [converting, setConverting] = useState(false);

  const orgById = React.useMemo(() => {
    const map = {};
    for (const o of organisations) if (o.id) map[o.id] = o;
    return map;
  }, [organisations]);

  // ── Fetch ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const orgId = searchParams.get('org');
    const shouldCreate = searchParams.get('create') === '1';
    if (orgId) {
      setCreateForm((prev) => ({ ...prev, organisation_id: orgId }));
    }
    if (shouldCreate && orgId) {
      setShowCreateDialog(true);
      const next = new URLSearchParams(searchParams);
      next.delete('create');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchOrgs = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations?include_inactive=false`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setOrganisations(data.organisations || []);
      }
    } catch {
      // non-fatal
    }
  }, []);

  const fetchCareLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/care-locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCareLocations(data.care_locations || []);
      }
    } catch {
      // non-fatal
    }
  }, []);

  const fetchRequests = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/staffing-requests`;
      if (statusFilter !== 'all') url += `?status=${statusFilter}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setRequests(data.staffing_requests || []);
      } else {
        toast.error('Failed to load staffing requests');
      }
    } catch {
      toast.error('Failed to load staffing requests');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);
  useEffect(() => { fetchCareLocations(); }, [fetchCareLocations]);
  useEffect(() => { setLoading(true); fetchRequests(); }, [fetchRequests]);

  // ── Drawer ─────────────────────────────────────────────────────────────────

  const openDrawer = (req) => {
    setDrawerRequest(req);
    setDrawerNotes(req.notes || '');
    setDrawerNotesOriginal(req.notes || '');
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/staffing-requests/${drawerRequest.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: drawerNotes }),
      });
      if (res.ok) {
        const data = await res.json();
        setDrawerRequest(data.staffing_request);
        setDrawerNotesOriginal(drawerNotes);
        toast.success('Notes saved');
        fetchRequests();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save notes');
      }
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setChangingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/staffing-requests/${drawerRequest.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const data = await res.json();
        setDrawerRequest(data.staffing_request);
        toast.success(`Marked ${STATUS_LABELS[newStatus]}`);
        fetchRequests();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update status');
      }
    } catch {
      toast.error('Failed to update status');
    } finally {
      setChangingStatus(false);
    }
  };

  // ── Verify Org ─────────────────────────────────────────────────────

  const handleVerifyOrg = async () => {
    const org = orgById[drawerRequest?.organisation_id];
    if (!org) return;
    setVerifyingOrg(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/organisations/${org.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_verified: true }),
      });
      if (res.ok) {
        toast.success('Organisation marked as verified');
        fetchOrgs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to verify organisation');
      }
    } catch {
      toast.error('Failed to verify organisation');
    } finally {
      setVerifyingOrg(false);
    }
  };

  // ── Cancel ─────────────────────────────────────────────────────────────────

  const openCancelDialog = (req) => {
    setCancelTarget(req);
    setCancelReason('');
  };

  const handleCancelConfirm = async () => {
    setCancelling(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/staffing-requests/${cancelTarget.id}/cancel`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: cancelReason.trim() || null }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        const closed = Number(data.shifts_cancelled) || 0;
        toast.success(
          closed > 0
            ? `${cancelTarget.request_code} cancelled — ${closed} unbooked shift${closed === 1 ? '' : 's'} closed`
            : `${cancelTarget.request_code} cancelled`
        );
        (data.warnings || []).forEach((w) => toast.warning(w));
        setCancelTarget(null);
        setCancelReason('');
        if (drawerRequest?.id === cancelTarget.id) setDrawerRequest(null);
        fetchRequests();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to cancel');
      }
    } catch {
      toast.error('Failed to cancel request');
    } finally {
      setCancelling(false);
    }
  };

  // ── Create ─────────────────────────────────────────────────────────────────

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.organisation_id) { toast.error('Select the care home organisation first'); return; }
    if (!createForm.request_type) { toast.error('Request type is required'); return; }
    if (!createForm.role_required.trim()) { toast.error('Role required is required'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...createForm,
        organisation_id: createForm.organisation_id,
        quantity: Number(createForm.quantity) || 1,
        requested_start_at: NEEDS_DATETIME.has(createForm.request_type) ? toIso(createForm.requested_start_at) : null,
        requested_end_at: NEEDS_DATETIME.has(createForm.request_type) ? toIso(createForm.requested_end_at) : null,
      };
      const res = await fetch(`${API}/staffing-requests`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Request created: ${data.staffing_request.request_code}`);
        setShowCreateDialog(false);
        setCreateForm(EMPTY_CREATE_FORM);
        fetchRequests();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to create staffing request');
      }
    } catch {
      toast.error('Failed to create staffing request');
    } finally {
      setSaving(false);
    }
  };

  // ── Convert ────────────────────────────────────────────────────────────────

  const openConvertDialog = (req) => {
    setDrawerRequest(null); // close drawer so dialogs don't stack
    setConvertTarget(req);
    const remaining = Math.max(1, getRequestQuantity(req) - getShiftsCreated(req));
    setConvertForm({
      start_at: toDatetimeLocal(req.requested_start_at),
      end_at: toDatetimeLocal(req.requested_end_at),
      location_text: orgById[req.organisation_id]?.name || '',
      notes: req.notes || '',
      care_location_id: '',
      count: remaining,
    });
  };

  const handleConvert = async (e) => {
    e.preventDefault();
    if (!convertForm.start_at || !convertForm.end_at) {
      toast.error('Start and end date/time are required');
      return;
    }
    const remaining = Math.max(0, getRequestQuantity(convertTarget) - getShiftsCreated(convertTarget));
    const count = Math.min(Math.max(1, Number(convertForm.count) || 1), remaining || 1);
    setConverting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        start_at: toIso(convertForm.start_at),
        end_at: toIso(convertForm.end_at),
        location_text: (convertForm.location_text || '').trim()
          || orgById[convertTarget.organisation_id]?.name
          || 'Care location',
        notes: convertForm.notes || null,
        care_location_id: convertForm.care_location_id || null,
      };
      const endpoint = count > 1
        ? `${API}/staffing-requests/${convertTarget.id}/convert-to-shifts`
        : `${API}/staffing-requests/${convertTarget.id}/convert-to-shift`;
      if (count > 1) {
        payload.count = count;
      }
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const data = await res.json();
        const orgId = convertTarget.organisation_id;
        const shiftsUrl = `/portal/shifts${orgId ? `?org=${orgId}` : ''}`;
        const shiftIds = (data.shifts || (data.shift ? [data.shift] : [])).map((s) => s.id).filter(Boolean);
        toast.success(data.message || 'Shift(s) created — assign a worker on Shifts', {
          action: { label: 'Open Shifts', onClick: () => navigate(shiftsUrl) },
        });
        (data.warnings || []).forEach((warning) => toast.warning(warning));
        setConvertTarget(null);
        setConvertForm(EMPTY_CONVERT_FORM);
        fetchRequests();
        if (shiftIds.length) {
          navigate(shiftsUrl);
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to convert to shift');
      }
    } catch {
      toast.error('Failed to convert to shift');
    } finally {
      setConverting(false);
    }
  };

  // ── Loading ────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const countByStatus = ALL_STATUSES.reduce((acc, s) => {
    acc[s] = requests.filter((r) => r.status === s).length;
    return acc;
  }, {});

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6" data-testid="staffing-requests-page">

      {/* ── Detail Drawer ──────────────────────────────────────────────────── */}
      <Dialog
        open={!!drawerRequest}
        onOpenChange={(open) => { if (!open) setDrawerRequest(null); }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {drawerRequest && (() => {
            const org = orgById[drawerRequest.organisation_id];
            const isUnverified = org?.is_verified === false;
            const isTerminal = TERMINAL_STATUSES.has(drawerRequest.status);
            return (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-lg font-bold text-primary" data-testid="drawer-code">
                      {drawerRequest.request_code}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[drawerRequest.status] || 'bg-gray-100 text-gray-500'}`}>
                      {requestStatusLabel(drawerRequest)}
                    </span>
                    {drawerRequest.created_via === 'public_form' && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                        Web Form
                      </span>
                    )}
                  </div>
                  <DialogDescription>
                    Received {fmtDateTime(drawerRequest.created_at)}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-5 pt-1">

                  {/* Unverified org warning */}
                  {isUnverified && (
                    <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="drawer-unverified-warning">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>This organisation was submitted via the public form and has not been verified. Confirm contact details before actioning this request.</span>
                      </div>
                      {org && (
                        <Button
                          size="sm"
                          className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={handleVerifyOrg}
                          disabled={verifyingOrg}
                          data-testid="drawer-verify-org-btn"
                        >
                          <ShieldCheck className="h-4 w-4 mr-1.5" />
                          {verifyingOrg ? 'Verifying…' : 'Mark as Verified'}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Organisation & Contact */}
                  <div>
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Organisation &amp; Contact</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <p className="text-xs text-text-muted">Organisation</p>
                        <p className="font-medium text-text-primary">
                          {org?.name || drawerRequest.organisation_id || '—'}
                          {isUnverified && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">Unverified</span>
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Type</p>
                        <p className="text-text-primary">{ORG_TYPE_LABELS[org?.organisation_type] || org?.organisation_type || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Contact name</p>
                        <p className="text-text-primary">{org?.primary_contact_name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Postcode</p>
                        <p className="text-text-primary">{org?.postcode || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Phone</p>
                        {org?.primary_contact_phone
                          ? <a href={`tel:${org.primary_contact_phone}`} className="text-primary hover:underline flex items-center gap-1"><Phone className="h-3 w-3" />{org.primary_contact_phone}</a>
                          : <p className="text-text-muted">—</p>}
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Email</p>
                        {org?.primary_contact_email
                          ? <a href={`mailto:${org.primary_contact_email}`} className="text-primary hover:underline flex items-center gap-1 truncate"><Mail className="h-3 w-3 flex-shrink-0" />{org.primary_contact_email}</a>
                          : <p className="text-text-muted">—</p>}
                      </div>
                    </div>
                  </div>

                  {/* Request Details */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Request Details</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <p className="text-xs text-text-muted">Role required</p>
                        <p className="font-medium text-text-primary">{drawerRequest.role_required || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Shifts created</p>
                        <p className="text-text-primary font-medium">{formatShiftProgress(drawerRequest)}</p>
                        <p className="text-sm font-medium mt-1">{formatCoverProgress(drawerRequest)}</p>
                        <p className="text-xs text-text-muted">Cover is booked only after staff are assigned.</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Request type</p>
                        <p className="text-text-primary">{REQUEST_TYPE_LABELS[drawerRequest.request_type] || drawerRequest.request_type || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Source</p>
                        <p className="text-text-primary">{drawerRequest.created_via === 'public_form' ? 'Web Form' : 'Manual'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Dates & Times */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Dates &amp; Times</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <p className="text-xs text-text-muted">Cover start date</p>
                        <p className="text-text-primary">{fmtDate(drawerRequest.start_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Cover end date</p>
                        <p className="text-text-primary">{fmtDate(drawerRequest.end_date)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Shift start</p>
                        <p className="text-text-primary">{fmtDateTime(drawerRequest.requested_start_at)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-text-muted">Shift end</p>
                        <p className="text-text-primary">{fmtDateTime(drawerRequest.requested_end_at)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Original Notes */}
                  {drawerRequest.notes && (
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Submitted Notes</p>
                      <div className="rounded-lg bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-text-primary whitespace-pre-wrap" data-testid="drawer-submitted-notes">
                        {drawerRequest.notes}
                      </div>
                    </div>
                  )}

                  {/* Internal Notes */}
                  <div className="border-t border-gray-100 pt-4">
                    <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">Internal Notes</p>
                    <Textarea
                      value={drawerNotes}
                      onChange={(e) => setDrawerNotes(e.target.value)}
                      rows={3}
                      placeholder="Add internal notes for this request…"
                      className="text-sm"
                      data-testid="drawer-notes"
                    />
                    <div className="flex justify-end mt-2">
                      <Button
                        size="sm"
                        onClick={handleSaveNotes}
                        disabled={savingNotes || drawerNotes === drawerNotesOriginal}
                        data-testid="drawer-save-notes"
                      >
                        {savingNotes ? 'Saving…' : 'Save Notes'}
                      </Button>
                    </div>
                  </div>

                  {/* Status Actions */}
                  {!isTerminal && (
                    <div className="border-t border-gray-100 pt-4">
                      <p className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-3">Actions</p>
                      <div className="flex flex-wrap gap-2">
                        {drawerRequest.status === 'open' && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-amber-300 text-amber-700 hover:bg-amber-50"
                            onClick={() => handleStatusChange('in_review')}
                            disabled={changingStatus}
                            data-testid="drawer-mark-in-review"
                          >
                            Mark In Review
                          </Button>
                        )}
                        {drawerRequest.request_type === 'permanent_recruitment' && ['open', 'in_review', 'matched'].includes(drawerRequest.status) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-300 text-green-700 hover:bg-green-50"
                            onClick={() => handleStatusChange('fulfilled')}
                            disabled={changingStatus}
                            data-testid="drawer-mark-fulfilled"
                          >
                            Mark Fulfilled
                          </Button>
                        )}
                        {canConvertRequest(drawerRequest) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-primary text-primary hover:bg-primary/5"
                            onClick={() => openConvertDialog(drawerRequest)}
                            disabled={changingStatus}
                            data-testid="drawer-convert"
                          >
                            <ArrowRightCircle className="h-4 w-4 mr-1.5" />
                            Convert to Shift ({formatShiftProgress(drawerRequest)})
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          onClick={() => openCancelDialog(drawerRequest)}
                          disabled={changingStatus}
                          data-testid="drawer-cancel"
                        >
                          <XCircle className="h-4 w-4 mr-1.5" />
                          Cancel Request
                        </Button>
                      </div>
                    </div>
                  )}

                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Cancel Confirmation Dialog ─────────────────────────────────────── */}
      <Dialog
        open={!!cancelTarget}
        onOpenChange={(open) => { if (!open) { setCancelTarget(null); setCancelReason(''); } }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Staffing Request</DialogTitle>
            <DialogDescription>
              {cancelTarget?.request_code} — {cancelTarget?.role_required}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <p className="text-sm text-text-muted">
              This will set the request status to <strong>Cancelled</strong> and close any linked shifts that have no staff booked. This action cannot be undone from here.
            </p>
            <p className="text-sm text-text-muted" data-testid="cancel-booked-note">
              If staff are booked on a linked shift, contact them directly and unassign or cancel that shift on the Shifts page first.
            </p>
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Reason <span className="font-normal text-text-muted">(optional)</span></Label>
              <Textarea
                id="cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                placeholder="e.g. Provider no longer needs cover"
                data-testid="cancel-reason"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => { setCancelTarget(null); setCancelReason(''); }}
                disabled={cancelling}
              >
                Keep Request
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelConfirm}
                disabled={cancelling}
                data-testid="cancel-confirm-btn"
              >
                {cancelling ? 'Cancelling…' : 'Yes, Cancel Request'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Convert-to-Shift Dialog ────────────────────────────────────────── */}
      <Dialog
        open={!!convertTarget}
        onOpenChange={(open) => { if (!open) { setConvertTarget(null); setConvertForm(EMPTY_CONVERT_FORM); } }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Convert to Shift</DialogTitle>
            <DialogDescription>
              {convertTarget?.request_code} — {convertTarget?.role_required}
            </DialogDescription>
          </DialogHeader>

          {convertTarget && (() => {
            const org = orgById[convertTarget.organisation_id];
            const isUnverified = org?.is_verified === false;
            const qty = getRequestQuantity(convertTarget);
            const created = getShiftsCreated(convertTarget);
            const remaining = Math.max(0, qty - created);
            return (
              <>
                {/* Reference block */}
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm space-y-1 text-blue-900" data-testid="convert-reference-block">
                  <p className="font-semibold mb-1">Original Request</p>
                  {org && <p><span className="text-blue-600 font-medium">Invoice client:</span> {org.name}</p>}
                  {org?.id && <p><span className="text-blue-600 font-medium">Billing organisation ID:</span> <span className="font-mono text-xs">{org.id}</span></p>}
                  {org?.primary_contact_phone && <p><span className="text-blue-600 font-medium">Phone:</span> {org.primary_contact_phone}</p>}
                  {org?.primary_contact_email && <p><span className="text-blue-600 font-medium">Email:</span> {org.primary_contact_email}</p>}
                  <p><span className="text-blue-600 font-medium">Role:</span> {convertTarget.role_required}</p>
                  <p><span className="text-blue-600 font-medium">Shift progress:</span> {created}/{qty} created{remaining > 0 ? ` · ${remaining} remaining` : ''}</p>
                  {convertTarget.notes && <p><span className="text-blue-600 font-medium">Notes:</span> {convertTarget.notes}</p>}
                  {convertTarget.requested_start_at && <p><span className="text-blue-600 font-medium">Requested start:</span> {fmtDateTime(convertTarget.requested_start_at)}</p>}
                  {convertTarget.requested_end_at && <p><span className="text-blue-600 font-medium">Requested end:</span> {fmtDateTime(convertTarget.requested_end_at)}</p>}
                  {convertTarget.start_date && <p><span className="text-blue-600 font-medium">Cover from:</span> {fmtDate(convertTarget.start_date)}</p>}
                  {convertTarget.end_date && <p><span className="text-blue-600 font-medium">Cover to:</span> {fmtDate(convertTarget.end_date)}</p>}
                </div>

                {remaining > 1 && (
                  <div className="space-y-2">
                    <div className="space-y-2">
                      <Label htmlFor="cv-count">Shifts to create</Label>
                      <Input
                        id="cv-count"
                        type="number"
                        min="1"
                        max={remaining}
                        value={convertForm.count}
                        onChange={(e) => setConvertForm({
                          ...convertForm,
                          count: Math.min(remaining, Math.max(1, Number(e.target.value) || 1)),
                        })}
                        data-testid="convert-shift-count"
                      />
                      <p className="text-xs text-text-muted">
                        Creates up to {remaining} identical shift{remaining !== 1 ? 's' : ''} with the same billing client ({org?.name || 'organisation on request'}).
                      </p>
                    </div>
                  </div>
                )}

                {/* Unverified warning */}
                {isUnverified && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800" data-testid="convert-unverified-warning">
                    <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>This organisation has not been verified. Confirm provider details before converting.</span>
                  </div>
                )}

                <form onSubmit={handleConvert} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="cv-start">Shift Start *</Label>
                      <Input
                        id="cv-start"
                        type="datetime-local"
                        value={convertForm.start_at}
                        onChange={(e) => setConvertForm({ ...convertForm, start_at: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cv-end">Shift End *</Label>
                      <Input
                        id="cv-end"
                        type="datetime-local"
                        value={convertForm.end_at}
                        onChange={(e) => setConvertForm({ ...convertForm, end_at: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cv-care-location">Care Location <span className="text-text-muted font-normal">(for QR verification)</span></Label>
                    <select
                      id="cv-care-location"
                      value={convertForm.care_location_id}
                      onChange={(e) => {
                        const id = e.target.value;
                        const loc = careLocations.find((l) => l.id === id);
                        setConvertForm({
                          ...convertForm,
                          care_location_id: id,
                          location_text: loc
                            ? `${loc.name}, ${loc.address_line_1}, ${loc.city}`
                            : convertForm.location_text,
                        });
                      }}
                      className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">— None (QR verification unavailable) —</option>
                      {careLocations.map((l) => (
                        <option key={l.id} value={l.id}>{l.name} — {l.city}</option>
                      ))}
                    </select>
                    {careLocations.length === 0 ? (
                      <p className="text-xs text-amber-700">No care locations exist. Create one on the Care Locations page to enable QR site verification.</p>
                    ) : !convertForm.care_location_id ? (
                      <p className="text-xs text-text-muted">QR site verification will not be available for this shift without a care location.</p>
                    ) : null}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cv-location">Location / Address *</Label>
                    <Input
                      id="cv-location"
                      value={convertForm.location_text}
                      onChange={(e) => setConvertForm({ ...convertForm, location_text: e.target.value })}
                      placeholder="Address or location name"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cv-notes">Shift Notes</Label>
                    <Textarea
                      id="cv-notes"
                      value={convertForm.notes}
                      onChange={(e) => setConvertForm({ ...convertForm, notes: e.target.value })}
                      rows={2}
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setConvertTarget(null); setConvertForm(EMPTY_CONVERT_FORM); }}
                      disabled={converting}
                    >
                      Cancel
                    </Button>
                    <Button type="submit" disabled={converting}>
                      {converting
                        ? 'Converting…'
                        : (Number(convertForm.count) > 1
                          ? `Create ${convertForm.count} Shifts`
                          : 'Create Shift')}
                    </Button>
                  </div>
                </form>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* ── Header + New Request Dialog ────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Staffing Requests</h1>
          <p className="text-sm text-text-muted mt-1">
            Review and action staffing requests from organisations — then convert to shifts
          </p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button data-testid="create-staffing-req-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Request
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>New Staffing Request</DialogTitle>
              <DialogDescription>Log a staffing request from an organisation.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="sr-org">Organisation *</Label>
                <select
                  id="sr-org"
                  value={createForm.organisation_id}
                  onChange={(e) => setCreateForm({ ...createForm, organisation_id: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                >
                  <option value="">Select care home / client…</option>
                  {organisations.map((o) => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
                <p className="text-[10px] text-text-muted">Log the care home under Organisations first if they are not listed.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="sr-type">Request Type *</Label>
                  <select
                    id="sr-type"
                    value={createForm.request_type}
                    onChange={(e) => setCreateForm({ ...createForm, request_type: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    required
                  >
                    <option value="">Select…</option>
                    {REQUEST_TYPES.map(({ value, label }) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sr-qty">Quantity</Label>
                  <Input
                    id="sr-qty"
                    type="number"
                    min="1"
                    value={createForm.quantity}
                    onChange={(e) => setCreateForm({ ...createForm, quantity: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sr-role">Role Required *</Label>
                <select
                  id="sr-role"
                  value={createForm.role_required}
                  onChange={(e) => setCreateForm({ ...createForm, role_required: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                  required
                >
                  <option value="">Select role…</option>
                  {STAFFING_ROLE_OPTIONS.map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>
              {NEEDS_DATETIME.has(createForm.request_type) && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sr-start">Requested Start</Label>
                    <Input
                      id="sr-start"
                      type="datetime-local"
                      value={createForm.requested_start_at}
                      onChange={(e) => setCreateForm({ ...createForm, requested_start_at: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="sr-end">Requested End</Label>
                    <Input
                      id="sr-end"
                      type="datetime-local"
                      value={createForm.requested_end_at}
                      onChange={(e) => setCreateForm({ ...createForm, requested_end_at: e.target.value })}
                    />
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="sr-notes">Notes</Label>
                <Textarea
                  id="sr-notes"
                  value={createForm.notes}
                  onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })}
                  rows={2}
                  placeholder="Optional notes"
                />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowCreateDialog(false); setCreateForm(EMPTY_CREATE_FORM); }}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? 'Creating…' : 'Create Request'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <StaffingPipelineGuide currentStep={4} />

      {/* ── Stats ─────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['open', 'in_review', 'matched', 'fulfilled'].map((s) => (
          <div key={s} className="p-4 rounded-xl bg-white border border-gray-100">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${STATUS_COLORS[s]?.split(' ')[0] || 'bg-gray-100'}`}>
                <Briefcase className={`h-5 w-5 ${STATUS_COLORS[s]?.split(' ')[1] || 'text-gray-500'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-text-primary">{countByStatus[s] || 0}</p>
                <p className="text-xs text-text-muted">{STATUS_LABELS[s]}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Status filter ─────────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === 'all' ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-text-muted hover:bg-gray-50'}`}
        >
          All
        </button>
        {ALL_STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${statusFilter === s ? 'bg-primary text-white' : 'bg-white border border-gray-200 text-text-muted hover:bg-gray-50'}`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {/* ── Table ─────────────────────────────────────────────────────────── */}
      {requests.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
          <Briefcase className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-text-primary mb-2">No Staffing Requests</h3>
          <p className="text-sm text-text-muted mb-4">
            {statusFilter !== 'all'
              ? `No ${STATUS_LABELS[statusFilter]?.toLowerCase()} requests`
              : 'Create your first staffing request'}
          </p>
          {statusFilter === 'all' && (
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Request
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Code</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Organisation</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Role</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Shifts</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Source</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Created</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-text-muted uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {requests.map((req) => {
                  const org = orgById[req.organisation_id];
                  const isUnverified = org?.is_verified === false;
                  const isTerminal = TERMINAL_STATUSES.has(req.status);
                  return (
                    <tr
                      key={req.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => openDrawer(req)}
                    >
                      <td className="px-4 py-4">
                        <span className="font-mono text-sm font-semibold text-primary">
                          {req.request_code}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-text-primary">
                            {org?.name || (req.organisation_id
                              ? <span className="text-text-muted text-xs">{req.organisation_id.slice(0, 8)}…</span>
                              : <span className="text-text-muted">—</span>)}
                          </span>
                          {isUnverified && (
                            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700" data-testid="table-unverified-badge">
                              Unverified
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-text-primary">{req.role_required}</td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                          {REQUEST_TYPE_LABELS[req.request_type] || req.request_type}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-sm text-text-muted text-center" data-testid={`shifts-progress-${req.id}`}>
                        <span className={req.coverage_complete ? 'font-medium text-green-700' : 'font-medium text-amber-700'}>
                          {formatCoverProgress(req)}
                        </span>
                        <span className="block text-xs text-text-muted">{formatShiftProgress(req)} shifts created</span>
                      </td>
                      <td className="px-4 py-4">
                        {req.created_via === 'public_form'
                          ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">Web Form</span>
                          : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Manual</span>}
                      </td>
                      <td className="px-4 py-4 text-xs text-text-muted whitespace-nowrap">{fmtDate(req.created_at)}</td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[req.status] || 'bg-gray-100 text-gray-500'}`}>
                          {requestStatusLabel(req)}
                        </span>
                      </td>
                      <td className="px-4 py-4" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem className="cursor-pointer" onClick={() => openDrawer(req)}>
                              <Eye className="h-4 w-4 mr-2 text-text-muted" />
                              View Details
                            </DropdownMenuItem>
                            {canConvertRequest(req) && (
                              <DropdownMenuItem className="cursor-pointer" onClick={() => openConvertDialog(req)}>
                                <ArrowRightCircle className="h-4 w-4 mr-2 text-primary" />
                                Convert to Shift ({formatShiftProgress(req)})
                              </DropdownMenuItem>
                            )}
                            {!isTerminal && (
                              <DropdownMenuItem className="text-red-600 cursor-pointer" onClick={() => openCancelDialog(req)}>
                                <XCircle className="h-4 w-4 mr-2" />
                                Cancel Request
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-50 bg-gray-50">
            <p className="text-xs text-text-muted">
              {requests.length} request{requests.length !== 1 ? 's' : ''}
              {statusFilter !== 'all' ? ` — ${STATUS_LABELS[statusFilter]}` : ''}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
